import React, { useEffect, useRef } from 'react';

interface Node {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
}

export const NetworkSphere: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const nodesRef = useRef<Node[]>([]);
  const rotationRef = useRef({ x: 0, y: 0 });
  const targetRotationRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const lastMouseRef = useRef({ x: 0, y: 0 });
  const scaleRef = useRef(0);
  const hasAnimatedRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const setCanvasSize = () => {
      const parent = canvas.parentElement;
      const parentSize = parent ? Math.min(parent.clientWidth, parent.clientHeight) : 280;
      const size = parentSize > 0 ? parentSize : 280;
      canvas.width = size;
      canvas.height = size;
    };

    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);

    const nodeCount = 60;
    const nodes: Node[] = [];

    const goldenRatio = (1 + Math.sqrt(5)) / 2;
    const angleIncrement = Math.PI * 2 * goldenRatio;

    for (let i = 0; i < nodeCount; i++) {
      const t = i / nodeCount;
      const inclination = Math.acos(1 - 2 * t);
      const azimuth = angleIncrement * i;
      const radius = 132;

      nodes.push({
        x: radius * Math.sin(inclination) * Math.cos(azimuth),
        y: radius * Math.sin(inclination) * Math.sin(azimuth),
        z: radius * Math.cos(inclination),
        vx: (Math.random() - 0.5) * 0.2,
        vy: (Math.random() - 0.5) * 0.2,
        vz: (Math.random() - 0.5) * 0.2
      });
    }

    nodesRef.current = nodes;

    const handleMouseDown = (e: MouseEvent) => {
      isDraggingRef.current = true;
      lastMouseRef.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (isDraggingRef.current) {
        const deltaX = e.clientX - lastMouseRef.current.x;
        const deltaY = e.clientY - lastMouseRef.current.y;

        targetRotationRef.current.y += deltaX * 0.01;
        targetRotationRef.current.x += deltaY * 0.01;

        lastMouseRef.current = { x: e.clientX, y: e.clientY };
      }
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
    };

    canvas.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (!hasAnimatedRef.current) {
        scaleRef.current += 0.02;
        if (scaleRef.current >= 1) {
          scaleRef.current = 1;
          hasAnimatedRef.current = true;
        }
      } else {
        scaleRef.current = 1;
      }

      if (!isDraggingRef.current) {
        targetRotationRef.current.y += 0.008;
      }

      rotationRef.current.x += (targetRotationRef.current.x - rotationRef.current.x) * 0.1;
      rotationRef.current.y += (targetRotationRef.current.y - rotationRef.current.y) * 0.1;

      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const baseScale = canvas.width / 400;
      const scale = baseScale * scaleRef.current;

      const sortedNodes = nodes
        .map(node => {
          const cosY = Math.cos(rotationRef.current.y);
          const sinY = Math.sin(rotationRef.current.y);
          const cosX = Math.cos(rotationRef.current.x);
          const sinX = Math.sin(rotationRef.current.x);

          let x = node.x;
          let y = node.y;
          let z = node.z;

          let tempZ = z * cosY - x * sinY;
          let tempX = z * sinY + x * cosY;
          z = tempZ;
          x = tempX;

          let tempY = y * cosX - z * sinX;
          tempZ = y * sinX + z * cosX;
          y = tempY;
          z = tempZ;

          return {
            node,
            screenX: centerX + x * scale,
            screenY: centerY + y * scale,
            z
          };
        })
        .sort((a, b) => a.z - b.z);

      sortedNodes.forEach((item, i) => {
        const alpha = Math.max(0.3, (item.z + 180) / 300);
        const size = Math.max(3, 6 * ((item.z + 180) / 300));

        for (let j = i + 1; j < sortedNodes.length; j++) {
          const other = sortedNodes[j];
          const dx = item.screenX - other.screenX;
          const dy = item.screenY - other.screenY;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 100 * scale) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(248, 189, 61, ${alpha * 0.3})`;
            ctx.lineWidth = 1.5;
            ctx.moveTo(item.screenX, item.screenY);
            ctx.lineTo(other.screenX, other.screenY);
            ctx.stroke();
          }
        }

        ctx.beginPath();
        ctx.arc(item.screenX, item.screenY, size, 0, Math.PI * 2);
        const gradient = ctx.createRadialGradient(item.screenX, item.screenY, 0, item.screenX, item.screenY, size);
        gradient.addColorStop(0, `rgba(248, 189, 61, ${alpha})`);
        gradient.addColorStop(1, `rgba(248, 189, 61, ${alpha * 0.6})`);
        ctx.fillStyle = gradient;
        ctx.fill();

        ctx.strokeStyle = `rgba(255, 255, 255, ${alpha * 0.5})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', setCanvasSize);
      canvas.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div className="w-full h-full flex items-center justify-center">
      <canvas
        ref={canvasRef}
        className="cursor-grab active:cursor-grabbing"
        style={{
          filter: 'drop-shadow(0 0 30px rgba(248, 189, 61, 0.5))',
          display: 'block',
        }}
      />
    </div>
  );
};
